import clsx from 'clsx';

export type UnderlineTabChip = {
  label: string;
  variant?: 'count' | 'optional';
};

export type UnderlineTab<T extends string> = {
  value: T;
  label: string;
  chip?: UnderlineTabChip;
};

type Props<T extends string> = {
  tabs: UnderlineTab<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  ariaLabel: string;
  idPrefix: string;
};

export const underlineTabButtonClassName = (isActive: boolean) =>
  clsx(
    'flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm transition-colors',
    isActive
      ? 'border-primary font-semibold text-secondary'
      : 'border-transparent font-normal text-grey-500 hover:text-grey-700'
  );

export const underlineTabChipClassName = (
  isActive: boolean,
  variant?: 'count' | 'optional'
) =>
  clsx(
    'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px]',
    variant === 'optional'
      ? 'bg-grey-100 font-normal text-grey-500'
      : isActive
        ? 'bg-secondary font-semibold text-white'
        : 'bg-grey-100 font-semibold text-grey-500'
  );

const UnderlineTabBar = <T extends string>({
  tabs,
  activeTab,
  onChange,
  ariaLabel,
  idPrefix
}: Props<T>) => (
  <div className="w-full overflow-x-auto border-b border-grey-200 bg-white">
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex min-w-max items-stretch px-4">
      {tabs.map((tab) => {
        const isActive = tab.value === activeTab;

        return (
          <button
            key={tab.value}
            id={`${idPrefix}-tab-${tab.value}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${idPrefix}-panel-${tab.value}`}
            onClick={() => onChange(tab.value)}
            className={underlineTabButtonClassName(isActive)}>
            {tab.label}
            {tab.chip && (
              <span
                className={underlineTabChipClassName(
                  isActive,
                  tab.chip.variant
                )}>
                {tab.chip.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

export default UnderlineTabBar;
